import { LitElement, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { html } from 'lit/html.js';
import { ThemeMixin } from '@cof/dxo-ui-theme';
import { DxoStyles } from './DxoStyles.js';
import { DxoStylesDark } from './DxoStylesDark.js';
import { MOBILE_MENU_ANIMATION_DURATION } from './constants.js';
import { closeIcon, hamburgerIcon, searchIcon } from './icons.js';
import { getLogout, getProfilePicture, isActiveHeaderTab, isActiveSubheaderTab, isActiveSubheaderDropdownLink } from './utils.js';

declare global {
  interface Window {
    caliperx2?: any;
  }
}

export interface Authentication {
    authenticated: boolean;
    login: () => void | string;
    logout: () => void | string;
    displayName?: string;
    profilePictureSrc?: string;
    default?: boolean;
}

export interface Tab {
    tabText: string;
    tabLink: string;
}

export interface SubheaderContent {
  tabs?: Array<{
    label: string;
    link: string;
    dropdownLinks?: Tab[];
  }>;
}

export interface MobileMenuSection {
  label: string;
  link: string;
  children?: MobileMenuSection[];
}

export class DxoUiHeader extends ThemeMixin(LitElement) {
  static styles = [DxoStyles, DxoStylesDark];
  @property({ type: String, attribute: 'id' }) id = '';

  @property({ type: String, attribute: 'logo-click-href' }) logoClickHref = '';

  @property({ type: String, attribute: 'logo-image-src' }) logoImageSrc = '';

  @property({ type: String, attribute: 'logo-alt-text' }) logoAltText = 'Logo';

  @property({ type: Array, attribute: 'headertabs' }) headertabs: Tab[] = []; // Object property cannot use dashes for multiple words

  @property({ type: String, attribute: 'active-header-tab' }) activeHeaderTab = '';

  @property({ type: Object, attribute: 'authentication' }) authentication: Authentication = {
    authenticated: false,
    login: () => {
      window.caliperx2?.authentication.functions.authenticateAndReturn(window.location.pathname);
    },
    logout: () => {
      window.caliperx2?.authentication.functions.logout();
    },
    displayName: '',
    default: true
  };

  @property({ type: Boolean }) displayLogoutDropdown = false;

  constructor() {
    super();
  }

  @property({ type: Object, attribute: 'subheadercontent' }) subheadercontent!: SubheaderContent; // Object property cannot use dashes for multiple words

  @property({ type: String, attribute: 'active-subheader-tab' }) activeSubheaderTab = '';

  @property({ type: Boolean, attribute: 'subheader-dropdown', converter: value => value === 'true' }) subheaderDropdown = false;

  @property({ type: String, attribute: 'active-subheader-dropdown-link' }) activeSubheaderDropdownLink = '';

  @property({ type: Boolean }) subheaderDropdownOpen = false;

  @property({ type: Boolean, attribute: 'mobile-menu', converter: value => value === 'true' }) mobileMenu = false;

  @property({ type: Array }) mobilemenucontent?: MobileMenuSection[];

  @property({ type: String, attribute: 'active-mobile-menu-dropdown-link' }) activeMobileMenuDropdownLink = '';

  @property({ type: Boolean }) mobileMenuOpen = false;

  @property({ type: Boolean }) mobileMenuClosing = false;

  @property({ type: Boolean }) mobileSlotOpen = false;

  @property({ type: Boolean }) hasSlotContent = false;

  @property({ type: Boolean }) hasCustomMobileMenu = false;

  @property({ type: Boolean, attribute: 'use-mobile-header-slot', converter: value => value === 'true' }) useMobileHeaderSlot = false;

  @property({ type: Boolean, attribute: 'hide-user-menu-options', converter: value => value === 'true' }) hideUserMenuOptions = false;

  @property({ type: Object }) intervalId: NodeJS.Timeout | null = null;

  @property({ type: Function }) unsubscribe = () => { };

  private _isConnected = false; // tracks if the component is connected to the DOM

  /**
   * Checks for existence of authentication service from caliperx2
   */
  waitForAuthenticationService(): Promise<boolean> {
    let attemptsLeft = 10;
    const poll = (resolve: (value: boolean) => void, reject: (reason?: string) => void) => {
      if (window.caliperx2?.authentication) {
        resolve(true);
      } else if (attemptsLeft === 0) {
        reject('Authentication service not found');
      } else {
        attemptsLeft--;
        setTimeout(() => poll(resolve, reject), 100);
      }
    };

    return new Promise(poll);
  }

  /**
   * Creates subscription fx for when authentication has been verified by backend
   * and sets client-side name, picture, auth.
   * 
   */
  setupAuthSubscription = () => {
    if (window.caliperx2?.authentication?.state?.isAuthChecked) {
      this.unsubscribe =
        window.caliperx2.authentication.state.isAuthChecked.subscribe(
          (isChecked: boolean) => {
            if (isChecked) {
              let currentAuth: Authentication = {...this.authentication}
              currentAuth.authenticated = window.caliperx2.authentication.state.getIsAuthenticated()
              if (currentAuth.authenticated) {
                currentAuth.displayName = `${window.caliperx2?.authentication.state.getUser().firstName} ${window.caliperx2?.authentication.state.getUser().lastName}`;
                currentAuth.profilePictureSrc = window.caliperx2?.authentication?.state?.getUserImage();
              }
              this.authentication = currentAuth;
            }
          }
        );
      return true;
    }
    return false;
  };

  /*
   * Potomac Tracking
   */
  firstUpdated() {
    const potomacCustomEvent = new CustomEvent('caliper-custom-interaction', {
      detail: {
        action: 'dxo-web-component-view',
        elementName: '@cof/dxo-ui-header',
        elementType: 'modal',
        elementValue: JSON.stringify({
          webComponentVersion: '2.1.0',
        }),
      },
    });
    document.dispatchEvent(potomacCustomEvent);
  }

  // mediaQueryList object to listen for screen size changes
  mediaQuery: MediaQueryList | null = null;

  connectedCallback() {
    super.connectedCallback();
    this._isConnected = true;

    // Remove any existing event listeners to prevent memory leaks
    this.removeEventListeners();

    if (this.authentication.default) {
      this.waitForAuthenticationService()
        .then((res) => {
          if (!this._isConnected) return; // bail if the component disconnects while waiting for auth service
          console.log('Authentication service loaded: ', res);
          this.setupAuthSubscription();
        })
        .catch((err) => {
          console.error('Error loading authentication service: ', err);
        });
    }

    // event listeners to close subheader dropdown modal when necessary
    window.addEventListener('popstate', this.handleNavigation);
    window.addEventListener('click', this.handleLinkClick);
    this.mediaQuery = window.matchMedia('(max-width: 1024px)');
    this.mediaQuery.addEventListener('change', this.handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._isConnected = false;
    if (this.authentication.default) {
      this.unsubscribe();
    }

    this.removeEventListeners();
  }

  removeEventListeners() {
    window.removeEventListener('popstate', this.handleNavigation);
    window.removeEventListener('click', this.handleLinkClick);
    this.mediaQuery?.removeEventListener('change', this.handleResize);
  }

  // close subheader dropdown modal when popstate event happens
  handleNavigation = () => {
    this.closeSubheaderDropdown();
  }

  // close subheader dropdown modal when link is clicked
  handleLinkClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (this.subheaderDropdownOpen && target.closest('a')) {
      this.closeSubheaderDropdown();
    }
  }

  // close subheader dropdown modal when screen size is set to mobile
  // close mobile menu or slot when screen size is set to desktop
  handleResize = (e: MediaQueryListEvent) => {
    if (e.matches && this.mobileMenu) {
      this.closeSubheaderDropdown();
    }
    if (!e.matches) {
      if (this.mobileMenuOpen) this.toggleMobileMenu(false);
      if (this.mobileSlotOpen) this.mobileSlotOpen = false;
    }
  }

  toggleMobileSlot() {
    this.mobileSlotOpen = !this.mobileSlotOpen;
    if (this.mobileSlotOpen && this.mobileMenuOpen) {
      this.toggleMobileMenu(false);
    }
  }

  handleSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this.hasSlotContent = slot.assignedNodes().length > 0;
  }

  handleCustomMobileMenuSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this.hasCustomMobileMenu = slot.assignedNodes().length > 0;
  }

  getLogo() {
    if (this.logoImageSrc.length > 0) {
      const logoClass = this.mobileMenu ? 'header-logo-link mobile-logo' : 'header-logo-link';
      const imgClass = this.mobileMenu ? 'header-logo mobile-logo' : 'header-logo';
      const searchOpenClass = this.mobileSlotOpen ? 'slot-open' : '';
      
      
      if (this.logoClickHref.length > 0) {
        return html`
          <a href=${this.logoClickHref} class="${logoClass} ${searchOpenClass}">
            <img class="header-logo" src=${this.logoImageSrc} alt=${this.logoAltText} />
          </a>`
      }
      return html`
        <img class="${imgClass} ${searchOpenClass}" src=${this.logoImageSrc} alt=${this.logoAltText} />`
    }
    return html``
  }

  getHeaderTabs() {
    if (this.headertabs && this.headertabs.length > 0) {
      const headerTabsClass = this.mobileMenu ? 'header-tabs hide-for-mobile' : 'header-tabs';

      return html`
        <div class="${headerTabsClass}">
          ${this.headertabs.map(tab => html`
            <a href=${tab.tabLink} class="header-tab-link ${isActiveHeaderTab(this.activeHeaderTab, tab.tabText)}">${tab.tabText}</a>
          `)}
        </div>
      `;
    }
    return html``;
  }

  getLogin() {
    if (typeof(this.authentication.login) === "function") {
      return html`
        <button class="login-button" @click=${this.authentication.login}>Sign In ➔</button>
      `
    } 
    return html`
      <a class="login-button" href="${this.authentication.login}">Sign In ➔</a>
    `
  }

  getProfileHeader() {
    return html`
      <div class="logout-dropdown-header">
        ${getProfilePicture(this.authentication)}
        <p class="logout-dropdown-name">${this.authentication.displayName}</p>
      </div>
    `;
  }

  getProfile() {
    return html`
      <div class="profile-container">
        <div class="profile-picture">
        ${getProfilePicture(this.authentication)}
        </div>
        <div class="dropdown-arrow">
          <svg
            width="8"
            height="5"
            viewBox="0 0 8 5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M7 1L4 4L1 1" stroke="white" />
          </svg>
        </div>
          <div class="logout-dropdown">
              ${this.getProfileHeader()}
              ${!this.hideUserMenuOptions ? html`
                <a href="/dashboard" class="dropdown-button">Apps Dashboard</a>
                <a href="/dashboard/user-settings" class="dropdown-button">Account Settings</a>
              ` : nothing}
            ${getLogout(this.authentication)}
          </div>
      </div>
    `
  }

  getSubheader() {
    const subheaderMobileClass = {
      'hide-for-mobile': this.mobileMenu
    }
    if (this.subheadercontent) {
      return html`
        <div class="subheader ${classMap(subheaderMobileClass)}">
          ${this.subheaderDropdown 
            ? this.subheadercontent.tabs?.map(tab => html`
                <div class="subheader-tab ${isActiveSubheaderTab(this.activeSubheaderTab, tab.label)}" @click=${() => this.openSubheaderDropdown()}>
                  <p>${tab.label}</p>
                </div>
              `)
            : this.subheadercontent.tabs?.map(tab => html`
                <a href=${tab.link} class="subheader-tab ${isActiveSubheaderTab(this.activeSubheaderTab, tab.label)}">
                  <p>${tab.label}</p>
                </a>
              `)
          }
        </div>
        ${this.subheaderDropdown && this.subheaderDropdownOpen ? this.getSubheaderDropdown() : html``}
      `;
    }
    return html``;
  }

  openSubheaderDropdown() {
    this.subheaderDropdownOpen = true;
  }

  getSubheaderDropdown() {
    return html`
      <div class="subheader-dropdown-overlay fade-in" @click=${() => this.closeSubheaderDropdown()}></div>
      <div class="subheader-dropdown drop-down">
        <div class="subheader-dropdown-content">
          ${this.subheadercontent.tabs?.map(tab => html`
            <div class="subheader-dropdown-column">
              ${tab.dropdownLinks?.map(link => html`
                <a href=${link.tabLink} class="subheader-dropdown-link ${isActiveSubheaderDropdownLink(this.activeSubheaderTab, this.activeSubheaderDropdownLink, tab.label, link.tabText)}"><span>${link.tabText}</span></a>
              `)}
            </div>
          `)}
        </div>
      </div>
    `;
  }


  closeSubheaderDropdown() {
    this.subheaderDropdownOpen = false;
  }

  toggleMobileMenu(animate = !this.hasCustomMobileMenu) {
    if (this.mobileMenuOpen) {
      if (animate) {
        this.mobileMenuClosing = true;
        setTimeout(() => {
          this.mobileMenuOpen = false;
          this.mobileMenuClosing = false;
        }, MOBILE_MENU_ANIMATION_DURATION);
      } else {
        this.mobileMenuOpen = false;
        this.mobileMenuClosing = false;
      }
    } else {
      this.mobileMenuOpen = true;
    }
  }

  getMobileMenu() {
    if (this.mobileMenuOpen || this.mobileMenuClosing) {
      return html`
        <slot name="custom-mobile-menu" @slotchange=${this.handleCustomMobileMenuSlotChange}></slot>
        ${!this.hasCustomMobileMenu ? html`
          <dxo-ui-header-mobile-menu
            theme=${this.theme}
            .mobilemenucontent=${this.mobilemenucontent}
            .authentication=${this.authentication}
            .activeTab=${this.activeHeaderTab}
            .activeSubTab=${this.activeSubheaderTab}
            .activeSubTabLink=${this.activeSubheaderDropdownLink}
            .activeMobileMenuDropdownLink=${this.activeMobileMenuDropdownLink}
            .closing=${this.mobileMenuClosing}
            @mobile-menu-link-click=${() => this.toggleMobileMenu(false)}
          ></dxo-ui-header-mobile-menu>
        ` : html``}
      `;
    }
  }


  render() {
    const headerClasses = {
      'hide-for-mobile': this.mobileMenu
    };
    const slotClasses = {
      'header-slot': true,
      'hide-for-mobile': this.mobileMenu, // hides slot on mobile
      'slot-open': this.mobileSlotOpen // slot is visible on mobile when opened
    };
    return html`
      <div class="dxo-ui-header" id=${this.id}>
        <div class="primary-header ${this.mobileMenu ? 'mobile-padding' : ''}">
          ${this.mobileMenu ? html`
              <button @click=${() => this.toggleMobileMenu()} class="mobile-button ${this.mobileSlotOpen ? 'slot-open' : ''}">
                ${this.mobileMenuOpen ? closeIcon : hamburgerIcon}
              </button>
            ` : html``}
          ${this.getLogo()}
          ${this.getHeaderTabs()}
          <slot name="header-slot" class=${classMap(slotClasses)} @slotchange=${this.handleSlotChange}></slot>
          ${this.mobileMenu && this.hasSlotContent && this.useMobileHeaderSlot ? html`
            <button @click=${() => this.toggleMobileSlot()} class="mobile-search-button" aria-label="search button">
              ${this.mobileSlotOpen ? closeIcon : searchIcon}
            </button>
          ` : html``}
          <div class="auth-actions ${classMap(headerClasses)}">
            ${this.authentication.authenticated ? this.getProfile() : this.getLogin()}
          </div>
        </div>
        ${this.subheadercontent ? this.getSubheader() : html``}
        ${this.mobileMenu ? this.getMobileMenu() : html``}
      </div>`
    ;
  }
}

